import { ref } from 'vue'

export type DatetimeLocale = 'nl-NL' | 'en-GB'

export type DateInput = Date | string | number | null | undefined

const rawLocale = ref<string>('nl')

export function setDatetimeLocale(locale: string | null | undefined): void {
  rawLocale.value = locale ?? 'nl'
}

export function resolveDatetimeLocale(raw?: string | null): DatetimeLocale {
  const tag = raw ?? rawLocale.value
  return String(tag).toLowerCase().startsWith('nl') ? 'nl-NL' : 'en-GB'
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/
const TIME_ONLY = /^\d{2}:\d{2}(:\d{2})?$/
const SPACED_DATETIME = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?$/

export function parseDateInput(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }

  if (typeof value === 'number') {
    const fromNumber = new Date(value)
    return Number.isNaN(fromNumber.getTime()) ? null : fromNumber
  }

  const raw = value.trim()
  if (raw === '') return null

  let parsed: Date
  if (DATE_ONLY.test(raw)) {
    parsed = new Date(`${raw}T00:00:00`)
  } else if (TIME_ONLY.test(raw)) {
    parsed = new Date(`1970-01-01T${raw}`)
  } else if (SPACED_DATETIME.test(raw)) {
    parsed = new Date(raw.replace(' ', 'T'))
  } else {
    parsed = new Date(raw)
  }

  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const formatterCache = new Map<string, Intl.DateTimeFormat>()

function formatter(options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const locale = resolveDatetimeLocale()
  const key = `${locale}|${JSON.stringify(options)}`
  let cached = formatterCache.get(key)
  if (!cached) {
    cached = new Intl.DateTimeFormat(locale, options)
    formatterCache.set(key, cached)
  }
  return cached
}

const pad = (value: number): string => String(value).padStart(2, '0')

export function formatDate(
  value: DateInput,
  options: { weekday?: boolean; year?: boolean } = {},
): string {
  const date = parseDateInput(value)
  if (!date) return ''

  const intlOptions: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' }
  if (options.year !== false) intlOptions.year = 'numeric'
  if (options.weekday) intlOptions.weekday = 'short'

  return formatter(intlOptions).format(date)
}

export function formatDateTime(value: DateInput): string {
  const date = parseDateInput(value)
  if (!date) return ''
  return `${formatDate(date)} ${formatTime(date)}`
}

export function formatTime(value: DateInput): string {
  const date = parseDateInput(value)
  if (!date) return ''
  return formatter({ hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(date)
}

export function formatDateLong(value: DateInput, options: { weekday?: boolean } = {}): string {
  const date = parseDateInput(value)
  if (!date) return ''

  const intlOptions: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }
  if (options.weekday) intlOptions.weekday = 'long'

  return formatter(intlOptions).format(date)
}

export function formatMonthYear(value: DateInput): string {
  const date = parseDateInput(value)
  if (!date) return ''
  return formatter({ month: 'short', year: 'numeric' }).format(date)
}

export function formatMonthYearLong(value: DateInput): string {
  const date = parseDateInput(value)
  if (!date) return ''
  return formatter({ month: 'long', year: 'numeric' }).format(date)
}

export function formatWeekday(
  value: DateInput,
  options: { width?: 'short' | 'long' } = {},
): string {
  const date = parseDateInput(value)
  if (!date) return ''
  return formatter({ weekday: options.width ?? 'short' }).format(date)
}

export function formatDateNumeric(value: DateInput): string {
  const date = parseDateInput(value)
  if (!date) return ''
  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}`
}

export function formatIsoWeek(value: DateInput): string {
  const date = parseDateInput(value)
  if (!date) return ''

  // Copy to the Thursday of the same ISO week, then count weeks from Jan 4th
  const thursday = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const isoDay = (thursday.getDay() + 6) % 7
  thursday.setDate(thursday.getDate() - isoDay + 3)

  const firstThursday = new Date(thursday.getFullYear(), 0, 4)
  const firstIsoDay = (firstThursday.getDay() + 6) % 7
  firstThursday.setDate(firstThursday.getDate() - firstIsoDay + 3)

  const week = 1 + Math.round((thursday.getTime() - firstThursday.getTime()) / (7 * 86400000))
  return `week ${week}`
}

export function toApiDate(value: DateInput): string {
  const date = parseDateInput(value)
  if (!date) return ''
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

export function toApiDateTime(value: DateInput): string {
  const date = parseDateInput(value)
  if (!date) return ''
  return date.toISOString()
}

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 60 * 60],
  ['month', 30 * 24 * 60 * 60],
  ['day', 24 * 60 * 60],
  ['hour', 60 * 60],
  ['minute', 60],
  ['second', 1],
]

export function formatRelativeTime(value: DateInput): string {
  const date = parseDateInput(value)
  if (!date) return ''

  const seconds = Math.round((date.getTime() - Date.now()) / 1000)
  const absolute = Math.abs(seconds)
  const formatter = new Intl.RelativeTimeFormat(resolveDatetimeLocale(), { numeric: 'auto' })

  for (const [unit, secondsPerUnit] of RELATIVE_UNITS) {
    if (absolute >= secondsPerUnit || unit === 'second') {
      return formatter.format(Math.round(seconds / secondsPerUnit), unit)
    }
  }

  return formatter.format(0, 'second')
}
